<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Avatar, IconMenuControl, IconMenuControlBadge, AppstoreO, Printer } from '@xpos/icons-svg';
import { ArrowDown } from '@element-plus/icons-vue';
import SwitchAccount from './SwitchAccount.vue';
import StaffShift from './StaffShift.vue';
import DeviceListDialog from './DeviceListDialog.vue';
import LanguageSelectorDialog from './LanguageSelectorDialog.vue';
import MerchantSelectDialog from './MerchantSelectDialog.vue';
import NetworkStatusPopover from './NetworkStatusPopover.vue';
import { useEmployeeStore } from '@/stores/employee';
import { useNetworkStatus } from '@/composables/useNetworkStatus';
import { useMenuHeightCalculation } from '@/composables/useMenuHeightCalculation';
import { useMenuPlatformChecks } from '@/composables/useMenuPlatformChecks';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { usePaymentStore } from '@/stores/payment';
import { eventBus } from '@/libs/EventBus';
import { filterMenusForPlatform, type MenuItem } from '@/utils/menu-config';
import { useAcl } from '../stores/acl';
import { PageNames } from '@/acl/permissions';
import { ElNotification } from 'element-plus';
import { Close } from '@element-plus/icons-vue';
import { useConfigStore } from '@/stores/config';
import { useUpdateStore } from '@/stores/update';
import { useShiftStore } from '@/stores/shift';
import Function from '@/views/pos/table/components/Function.vue';
import MoreMenuDropdown from './menu/MoreMenuDropdown.vue';
import { handleUnRegisterDevice } from '@/utils/device.utils';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { useMerchantOutlets } from '@/composables/useMerchantOutlets';
import { handleMenuAction } from '@/services/menuActionService';
import * as Sentry from '@sentry/vue';
import { useInvoiceStore } from '@/stores/invoice';
import PayAtCounterBadge from './PayAtCounterBadge.vue';

const emit = defineEmits<{ 'menu-click': []; 'menu-width-change': [boolean] }>();

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const employeeStore = useEmployeeStore();
const paymentStore = usePaymentStore();
const configStore = useConfigStore();
const updateStore = useUpdateStore();
const shiftStore = useShiftStore();
const acl = useAcl();
employeeStore.load();

// Set Sentry tags for error filtering
const merchantUid = secureStorage.getItem('merchant_uid');
const deviceUid = secureStorage.getItem('device_uid');
if (merchantUid) Sentry.setTag('merchant_id', merchantUid);
if (deviceUid) Sentry.setTag('device_id', deviceUid);

const visibleFunctionDialog = ref(false);
const { visibleStaffShift } = storeToRefs(paymentStore);
const { currentShift } = storeToRefs(shiftStore);
const { onLine, networkSpeed, networkLatency, showNetworkAlert, networkLevel, networkStatusKey, networkPopperClass, dismissNetworkAlert } = useNetworkStatus();
const { platform, isMobileWeb, showLogo, showDeviceButton, showSwitchAccount, showNetworkStatus, showFunctionButton, showWideAccountButton } = useMenuPlatformChecks();

// Platform-filtered menu items
const menus = computed(() => filterMenusForPlatform(platform.value, (configStore.appConfig?.config as any)?.system?.enable_appointment_module ?? false));
const current = ref(route.path || '/pos');

// Menu height calculation
const { menuContainerRef, bottomContainerRef, visibleMenus, hiddenMenus, updateVisibleMenus } = useMenuHeightCalculation(menus, current);

// Wide mode based on platform
const isWideMode = computed(() => platform.value === 'desktop-web' || platform.value === 'mobile-web');

const showSwitchAccountModal = ref(false);
const showDeviceListModal = ref(false);
const showLanguageModal = ref(false);
const showMerchantSelectModal = ref(false);

const handleLogout = async () => { await handleUnRegisterDevice(); };
const { outlets, merchantName, logoPath, loadMerchants, selectMerchant } = useMerchantOutlets();

// Hidden crash test: tap logo 5 times within 3 seconds to throw a test error to Sentry
let logoTapCount = 0;
let logoTapTimer: ReturnType<typeof setTimeout> | null = null;
const handleLogoTap = () => {
  logoTapCount++;
  if (logoTapTimer) clearTimeout(logoTapTimer);
  logoTapTimer = setTimeout(() => { logoTapCount = 0; }, 3000);
  if (logoTapCount >= 5) { logoTapCount = 0; throw new Error('Sentry test crash'); }
};

watch(isWideMode, (v) => { emit('menu-width-change', v); }, { immediate: true });

// Management menu requires at least one accessible sub-page in addition to the route permission
const hasAnyManagementPermission = computed(() => Object.values(PageNames).some(p => acl.checkPermission(p)));
const skipMenuPermVisual = computed(() => isMobileWeb.value || platform.value === 'desktop-web');
// Pure check for visual grey; used by clickMenu to fire notification when denied
const menuDenied = (menu: MenuItem) => {
  if (skipMenuPermVisual.value) return false;
  if (menu.permission && !acl.checkPermission(menu.permission)) return true;
  return menu.path === '/management' && !hasAnyManagementPermission.value;
};
const clickMenu = async (menu: MenuItem) => {
  if (menuDenied(menu)) { eventBus.emit('permission denied'); return; }
  emit('menu-click');
  await router.push(menu.path);
  if (hiddenMenus.value.includes(menu) && hiddenMenus.value.length > 0) {
    await nextTick();
    updateVisibleMenus();
  }
};

const handlePermissionDenied = () => {
  if (showSwitchAccountModal.value) return;
  ElNotification({ customClass: 'x-notification__error', title: t('common.error'), message: t('common.permissionDenied'), duration: 7000, icon: Close });
  showSwitchAccountModal.value = true;
};

watch(() => route.path, (newPath) => {
  current.value = newPath;
  nextTick(() => { updateVisibleMenus(); });
});

// Manage badge count starts once enable_pay_at_counter_badge is on, also when enabled later (settings save doesn't reload); mobile-web has no Manage menu
watch(() => !isMobileWeb.value && !!configStore.appConfig?.config?.order?.enable_pay_at_counter_badge, on => on && useInvoiceStore().startPayAtCounterCountLiveQuery(), { immediate: true });

onMounted(async () => {
  eventBus.on('permission denied', handlePermissionDenied);
  await loadMerchants();
  await configStore.startLiveQuery();
  updateStore.startUpdateTimer();
});

onUnmounted(() => {
  if (logoTapTimer) { clearTimeout(logoTapTimer); logoTapTimer = null; }
  updateStore.stopUpdateTimer();
  eventBus.off('permission denied', handlePermissionDenied);
});

const handleAction = (action: string, ...args: any[]) => handleMenuAction(action, { t, currentShift, visibleStaffShift }, ...args);
</script>
<template>
  <div class="menu-wrapper" :class="{ 'wide-mode': isWideMode }">
    <div class="h-full">
      <div class="flex flex-col h-full" ref="menuContainerRef">
        <!-- Header -->
        <div class="header-section" :class="{ 'wide-header': isWideMode }">
          <template v-if="!isWideMode">
            <div class="logo-container">
              <img :src="logoPath" class="logo-image" @click="handleLogoTap" />
            </div>
          </template>
          <template v-else>
            <div class="wide-header-content">
              <div class="logo-container-wide" v-if="showLogo">
                <img :src="logoPath" class="logo-image-wide" @click="handleLogoTap" />
              </div>
              <div class="merchant-info" @click="showMerchantSelectModal = true">
                <div class="merchant-name">{{ merchantName }}</div>
                <el-icon v-if="isMobileWeb && outlets.length > 1" class="dropdown-icon"><ArrowDown /></el-icon>
              </div>
            </div>
          </template>
        </div>

        <!-- Menu items -->
        <div class="flex-1 overflow-hidden">
          <ul class="h-full">
            <li v-for="menu in visibleMenus" :key="menu.path" :class="{ 'bg-white primary-color': menu.path == current, 'text-white': menu.path != current, 'menu-item-wide': isWideMode, 'menu-item-narrow': !isWideMode, 'menu-denied': menuDenied(menu) }" class="menu-item cursor-pointer" @click="clickMenu(menu)">
              <template v-if="!isWideMode">
                <div class="h-3 bg-primary-color w-full rounded-br-[20px]"></div>
                <div class="h-[65px] flex justify-center items-center flex-col font-medium">
                  <el-icon size="30"><component :is="menu.icon"></component><PayAtCounterBadge v-if="menu.path === '/management'" class="-top-1 -right-2" /></el-icon>{{ t(menu.name) }}
                </div>
              </template>
              <template v-else>
                <div class="h-3 bg-primary-color w-full rounded-br-[20px]"></div>
                <div class="wide-menu-content">
                  <el-icon size="30" class="menu-icon"><component :is="menu.icon"></component><PayAtCounterBadge v-if="menu.path === '/management'" class="-top-1 -right-2" /></el-icon>
                  <span class="menu-text">{{ t(menu.name) }}</span>
                </div>
                <div v-if="menu.path == current" class="selected-corner"></div>
              </template>
            </li>
            <MoreMenuDropdown v-if="hiddenMenus.length > 0" :menus="hiddenMenus" :wide="isWideMode" @select="clickMenu" />
          </ul>
        </div>

        <!-- Bottom buttons -->
        <div ref="bottomContainerRef" class="bottom-section" :class="{ 'bottom-wide': isWideMode }">
          <template v-if="!isWideMode">
            <button v-if="showDeviceButton" class="w-[50px] h-[50px] bg-white rounded-[10px] mb-2.5 primary-color flex justify-center items-center text-xl relative" @click="showDeviceListModal = true">
              <div class="relative inline-flex items-center justify-center w-full h-full">
                <el-icon><IconMenuControl /></el-icon>
                <IconMenuControlBadge v-if="updateStore.hasUpdate" class="w-[22px] h-[22px] absolute top-[4px] right-[2px]"/>
              </div>
            </button>
            <button v-if="showSwitchAccount" class="w-[50px] h-[50px] bg-white rounded-[10px] mb-2.5 primary-color flex justify-center items-center text-xl" @click="showSwitchAccountModal = true">
              <el-icon><Avatar /></el-icon>
            </button>
            <NetworkStatusPopover v-if="showNetworkStatus" v-model:visible="showNetworkAlert" :on-line="onLine" :network-speed="networkSpeed" :network-latency="networkLatency" :network-level="networkLevel" :network-status-key="networkStatusKey" :network-popper-class="networkPopperClass" @dismiss="dismissNetworkAlert" />
            <button v-if="showFunctionButton" class="w-[50px] h-[50px] bg-white rounded-[10px] primary-color flex justify-center items-center text-xl" @click="visibleFunctionDialog = true">
              <el-icon><AppstoreO /></el-icon>
            </button>
          </template>
          <template v-else>
            <div class="wide-bottom-buttons">
              <button v-if="showDeviceButton" class="wide-button" @click="showDeviceListModal = true">
                <el-icon class="button-icon"><Printer /></el-icon>
                <span class="button-text">{{ t('buttons.deviceList') }}</span>
              </button>
              <button v-if="showWideAccountButton" class="wide-button" @click="showSwitchAccountModal = true">
                <el-icon class="button-icon"><Avatar /></el-icon>
                <span class="button-text">{{ t('buttons.manageAccount') }}</span>
              </button>
              <NetworkStatusPopover v-if="showNetworkStatus" v-model:visible="showNetworkAlert" :on-line="onLine" :network-speed="networkSpeed" :network-latency="networkLatency" :network-level="networkLevel" :network-status-key="networkStatusKey" :network-popper-class="networkPopperClass" :wide="true" @dismiss="dismissNetworkAlert" />
              <button v-if="showFunctionButton" class="wide-button" @click="visibleFunctionDialog = true">
                <el-icon class="button-icon"><AppstoreO /></el-icon>
                <span class="button-text">{{ t('table.buttons.function') }}</span>
              </button>
              <button class="wide-button language-button" @click="showLanguageModal = true">
                <el-icon class="button-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z" />
                  </svg>
                </el-icon>
                <span class="button-text">{{ t('buttons.language') }}</span>
              </button>
              <button class="wide-button logout-button" @click="handleLogout">
                <el-icon class="button-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                  </svg>
                </el-icon>
                <span class="button-text">{{ t('buttons.logout') }}</span>
              </button>
            </div>
          </template>
        </div>
      </div>

      <!-- Modals -->
      <SwitchAccount v-model="showSwitchAccountModal" @close="showSwitchAccountModal = false" />
      <StaffShift v-model="visibleStaffShift" />
      <DeviceListDialog v-model="showDeviceListModal" @close="showDeviceListModal = false" />
      <LanguageSelectorDialog v-model="showLanguageModal" @close="showLanguageModal = false" />
      <MerchantSelectDialog v-model="showMerchantSelectModal" :outlets="outlets" @close="showMerchantSelectModal = false" @select="selectMerchant" />
    </div>
    <x-dialog v-model="visibleFunctionDialog" width="770px" :title="t('table.buttons.function')">
      <Function @action="handleAction" :hasTitle="false" :hasDiscount="false" :functions="['startShift', 'endShift', 'payIn', 'payOut', 'openDrawer']" />
    </x-dialog>
  </div>
</template>
<style lang="sass" scoped>
.menu-wrapper
  height: 100%
  width: 100%
  transition: all 0.3s ease
  &.wide-mode
    width: 200px

.header-section
  position: relative
  margin-bottom: 20px
  &.wide-header
    margin-bottom: 0

.logo-container
  display: flex
  justify-content: center
  align-items: center
  padding: 10px

.logo-image
  width: 44px
  height: 44px
  border-radius: 50%
  background: #F3F3F3
  padding: 3px

.wide-header-content
  display: flex
  align-items: center
  gap: 12px
  padding: 15px 20px
  border-bottom: 1px solid rgba(255, 255, 255, 0.1)

.logo-container-wide
  display: flex
  align-items: center

.logo-image-wide
  width: 50px
  height: 50px
  border-radius: 50%
  background: white

.merchant-info
  display: flex
  align-items: center
  justify-content: space-between
  cursor: pointer
  gap: 8px
  flex: 1

.merchant-name
  color: white
  font-size: 14px
  font-weight: 500
  display: -webkit-box
  -webkit-box-orient: vertical
  -webkit-line-clamp: 2
  overflow: hidden
  line-height: 1.4
  max-height: calc(1.4em * 2)

.dropdown-icon
  color: white

.menu-item
  text-align: center
  width: 100%
  font-size: 12px
  transition: all 0.3s ease
  &.menu-denied
    opacity: 0.4
  &.menu-item-narrow
    height: 77px
  &.menu-item-wide
    height: 77px
    text-align: left
    position: relative
    &.bg-white
      background: white !important
      &:hover
        background: #f5f5f5 !important
      .wide-menu-content
        color: #52599D
      .menu-icon
        color: #52599D

.wide-menu-content
  display: flex
  align-items: center
  padding: 0 20px
  height: 100%
  margin-left: 0
  padding-bottom: 10px

.menu-icon
  flex-shrink: 0
  margin-right: 12px
  display: flex
  align-items: center
  justify-content: center

.menu-text
  flex: 1
  text-align: center
  font-size: 14px
  font-weight: 500
  text-transform: uppercase

.bottom-section
  display: flex
  flex-direction: column
  align-items: center
  padding-bottom: 10px
  margin-top: auto
  &.bottom-wide
    padding: 0

.wide-bottom-buttons
  display: flex
  flex-direction: column
  width: 100%
  gap: 0

.wide-button
  display: flex
  align-items: center
  gap: 12px
  padding: 12px 16px
  background: white
  border: none
  border-radius: 12px
  margin: 4px 16px
  cursor: pointer
  transition: all 0.2s ease
  text-align: left
  min-height: 48px
  &:hover
    background: #f5f5f5
  &.language-button
    margin-bottom: 4px
  &.logout-button
    margin-bottom: 16px

.button-icon
  width: 20px
  height: 20px
  color: #52599D

.button-text
  font-size: 14px
  color: #52599D
  font-weight: 500
</style>
<style lang="sass">
// Network popover styles
.network-popover
  border-radius: 10px !important
  overflow: visible !important
  .el-popper__arrow
    display: block !important
    width: 16px !important
    height: 16px !important
    &::before
      width: 16px !important
      height: 16px !important
      border-width: 2px !important
  &[data-popper-placement^=right] > .el-popper__arrow
    left: -9px !important
  &--poor, &--moderate, &--healthy
    border-width: 2px !important
  $states: ('offline': #E63030, 'poor': #E63030, 'moderate': #FFB800, 'healthy': #80BA5C)
  @each $name, $color in $states
    &--#{$name}
      --c: #{$color}
      background: linear-gradient(0deg, rgba($color, 0.1), rgba($color, 0.1)), #FFF !important
      border-color: var(--c) !important
      box-shadow: 0 0 5px 2px rgba($color, 0.4) !important
      .network-title
        color: var(--c)
      .network-subtitle
        color: #3A3A3A
      .el-popper__arrow::before
        background: mix($color, #fff, 10%) !important
        border-color: var(--c) !important
</style>
